import { Badge, Button, Flex, Grid, GridItem, Text, useDisclosure } from '@chakra-ui/react';

import { format, parseISO } from 'date-fns';

import EditEmployeeTerminationDateModal from 'app/components/ManagePeople/ManageDefaultEmployees/EditEmployeeTerminationDate/EditEmployeeTerminationDateModal';
import TerminateEmployeeModal from 'app/components/ManagePeople/ManageDefaultEmployees/TerminateEmployee/TerminateEmployeeModal';

import { Status } from 'app/constants/individual';
import { CalendarOutline, DEFAULT_DATE_FORMAT, Individual } from '@aslanpay/portal-common-lib';

interface EmployeeProfileDetailsProps {
  employee: Individual;
  onInvalidateQuery: () => void;
}

function EmployeeProfileDetails({ employee, onInvalidateQuery }: EmployeeProfileDetailsProps) {
  const {
    isOpen: isTerminateOpen,
    onOpen: onOpenTerminate,
    onClose: onCloseTerminate,
  } = useDisclosure();

  const {
    isOpen: isEditTerminationDateOpen,
    onOpen: onOpenEditTerminationDate,
    onClose: onCloseEditTerminationDate,
  } = useDisclosure();

  // A future leave date doesn't change status until the date actually arrives (there's no ODP module
  // to restrict early for gifting-only employees), so this must check terminationDate alone — gating
  // on status additionally (as the standard employee profile never does) hid genuinely-set future
  // leave dates, since IndividualStatus has no 'LEAVING' value at all.
  const isLeaveDateSet = Boolean(employee.terminationDate);

  return (
    <>
      <TerminateEmployeeModal
        id={employee.id}
        isOpen={isTerminateOpen}
        onClose={onCloseTerminate}
        onTerminate={onInvalidateQuery}
      />
      <EditEmployeeTerminationDateModal
        id={employee.id}
        isOpen={isEditTerminationDateOpen}
        onClose={onCloseEditTerminationDate}
        onEditTerminate={onInvalidateQuery}
      />

      <Flex
        flexDir="column"
        borderRadius="10px"
        border="1px solid"
        borderColor="brand.lightGray1"
        mx={{ base: 0, md: '60px', xl: '100px' }}
        flexWrap="wrap"
      >
        <Flex w="full" justifyContent="start" p="20px">
          <Text color="brand.purple" textStyle="h5Bold">
            Profile
          </Text>

          {employee.status && (
            <Badge colorScheme={`badge.${Status[employee.status].color}`} ml="auto">
              {Status[employee.status].value}
            </Badge>
          )}
        </Flex>
        <Flex
          w="full"
          flexDir={{ base: 'column', md: 'row' }}
          justifyContent="start"
          p="20px"
          borderTop="1px solid"
          borderColor="brand.lightGray1"
          gap="20px"
        >
          <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
            General details
          </Text>

          <Grid templateColumns="1fr 1fr" rowGap="15px" columnGap="20px" wordBreak="break-word">
            <GridItem>
              <Flex gap="6px" flexDir="column">
                <Text color="brand.darkGray" textStyle="extraSmallRegular" flexShrink={0}>
                  First name
                </Text>
                <Text py="15px" textStyle="smallMedium">
                  {employee.firstName || '—'}
                </Text>
              </Flex>
            </GridItem>
            <GridItem>
              <Flex gap="6px" flexDir="column">
                <Text color="brand.darkGray" textStyle="extraSmallRegular">
                  Last name
                </Text>
                <Text py="15px" textStyle="smallMedium">
                  {employee.lastName || '—'}
                </Text>
              </Flex>
            </GridItem>
            <GridItem>
              <Flex gap="6px" flexDir="column">
                <Text color="brand.darkGray" textStyle="extraSmallRegular">
                  Job title
                </Text>
                <Text py="15px" textStyle="smallMedium">
                  {employee.jobTitle || '—'}
                </Text>
              </Flex>
            </GridItem>
            <GridItem>
              <Flex gap="6px" flexDir="column">
                <Text color="brand.darkGray" textStyle="extraSmallRegular">
                  Company email
                </Text>
                <Text py="15px" textStyle="smallMedium">
                  {employee.email}
                </Text>
              </Flex>
            </GridItem>
          </Grid>
        </Flex>
        <Flex
          w="full"
          flexDir={{ base: 'column', md: 'row' }}
          justifyContent="start"
          alignItems={{ base: 'start', md: 'center' }}
          p="20px"
          borderTop="1px solid"
          borderColor="brand.lightGray1"
          gap="20px"
        >
          <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
            Leave date
          </Text>

          <Flex
            w="full"
            flexDir={{ base: 'column', md: 'row' }}
            justifyContent="start"
            alignItems={{ base: 'start', md: 'center' }}
            rowGap="20px"
            columnGap="10px"
          >
            <Text color="brand.black" textStyle="smallRegular">
              {isLeaveDateSet && employee.terminationDate
                ? format(parseISO(employee.terminationDate), DEFAULT_DATE_FORMAT)
                : '—'}
            </Text>

            <Button
              variant="link"
              size="sm"
              leftIcon={<CalendarOutline boxSize="20px" />}
              ml={{ base: 0, md: 'auto' }}
              onClick={isLeaveDateSet ? onOpenEditTerminationDate : onOpenTerminate}
              isDisabled={employee.status === 'LEFT'}
            >
              {isLeaveDateSet ? 'Change leave date' : 'Set leave date'}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}

export default EmployeeProfileDetails;
